import React, { useRef, useState, useEffect } from "react";
import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Mail, Shield, Eye, Type, Contrast, Repeat, Bell, Clock, Phone, HelpCircle, ExternalLink } from 'lucide-react';
import { motion } from "framer-motion";
import { toast } from "sonner";
// import AxiosInstance from "@/lib/axios"; // <-- make sure your path is correct
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import AxiosInstance from "@/utils/AxiosInstance";
import { PATIENT_TIMEZONES } from "@/constants/usOrgTimezones";
import { useAuth } from "@/contexts/use-auth";
import {
  canShowProfileSupport,
  openFreshdeskPopup,
} from "@/utils/freshdeskSupport";
import { Dialog, DialogHeader } from "@/components/ui/dialog";
import { DialogContent, DialogTitle } from "@radix-ui/react-dialog";
import MyDialogContent from "@/components/MyDialogContent";
import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import RhfSelect from "@/components/rhfFields/RhfSelect";
import { optOutSchema, ProfileSchema } from "@/utils/ValidataionSchemas";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import RhfPhone from "@/components/rhfFields/RhfPhone";
import * as yup from "yup";
import UserSwitch from "./UserSwitch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { SnoozeCustomDateTimeField } from "./CaretakerProfile";
import { Checkbox } from "@/components/ui/checkbox";



/** Parse quiet-hours `HH:mm` (24h) for display; supports `H:mm`. */
function parseHHMMForDisplay(hhmm: string): { h24: number; minute: number } | null {
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h24 = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  if (
    Number.isNaN(h24) ||
    Number.isNaN(minute) ||
    h24 < 0 ||
    h24 > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return { h24, minute };
}

/** Show e.g. `3:32 PM` while state/API remain `15:32`. */
function formatHHmmAs12h(hhmm: string) {
  const p = parseHHMMForDisplay(hhmm);
  if (!p) return "";
  const d = new Date(1970, 0, 1, p.h24, p.minute, 0, 0);
  return format(d, "h:mm a", { locale: enUS });
}

const dummyUser = {
  name: "Priyanshu Dhawan",
  email: "priyanshu@example.com",
  role: "Admin",
};

const ALL_PROGRAM_OPTIONS = [
  { label: "CCM", value: "ccm" },
  { label: "PCM", value: "pcm" },
  { label: "RPM", value: "rpm" },
  { label: "BHI", value: "bhi" },
];

export default function Profile() {
  const { user, displayName, setUser, profile, setProfile, userSwitchSessionData } = useAuth();

  // Program dropdown: only show programs the user has (from profile API programs: ["pcm","rpm"])
  const programOptions = (profile?.programs && Array.isArray(profile.programs))
    ? ALL_PROGRAM_OPTIONS.filter((opt) => profile.programs.includes(opt.value))
    : ALL_PROGRAM_OPTIONS;

  const [name, setName] = useState(displayName || user?.name);

  const [fontSize, setFontSize] = useState(
    localStorage.getItem("fontSize") || "medium",
  );
  const [highContrast, setHighContrast] = useState(
    localStorage.getItem("highContrast") === "true",
  );
  const [screenReaderMode, setScreenReaderMode] = useState(
    localStorage.getItem("screenReaderMode") === "true",
  );

  // 🔐 Password states (for non-admin users only)

  const [loadingPass, setLoadingPass] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingOptOut ,setLoadingOptOut] = useState(false);
  const [openOptOut, setOpenOptOut] = useState(false);
  const [isUserSwitchOpen, setIsUserSwitchOpen] = useState(false);
  const [optReason, setOptReason] = useState("");
  const hasLinkedProfilesForSwitch =
    (userSwitchSessionData?.linkedProfiles?.length || 0) > 1;

  // =========================
  // Patient Notification Settings (API-backed)
  // =========================
  const isPatient = user?.role === "patient";
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);

  const [notifTimezone, setNotifTimezone] = useState("");
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [clearSnooze, setClearSnooze] = useState(false);

  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
  const [quietHoursStartTime, setQuietHoursStartTime] = useState("");
  const [quietHoursEndTime, setQuietHoursEndTime] = useState("");

  const [snoozePreset, setSnoozePreset] = useState<
    "" | "1h" | "4h" | "12h" | "24h" | "custom"
  >("");
  const [snoozeCustomStartLocal, setSnoozeCustomStartLocal] = useState("");
  const [snoozeCustomEndLocal, setSnoozeCustomEndLocal] = useState("");
  const quietStartRef = useRef<HTMLInputElement | null>(null);
  const quietEndRef = useRef<HTMLInputElement | null>(null);

  const [eventPrefs, setEventPrefs] = useState({
    NO_RPM_READING_3_DAYS: true,
    NO_CARE_PLAN_MONTHLY: true,
    RPM_ACHIEVEMENT_16PLUS: true,
    DEVICE_READING_RECEIVED: true,
    DEVICE_READING_RECEIVED_sendDuringQuietHours: false,
  });

  const toIsoOrEmpty = (local: string) => {
    if (!local) return "";
    // datetime-local → Date ISO (UTC). Good enough for UI; backend uses ISO.
    const d = new Date(local);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString();
  };

  const openNativePicker = (el: HTMLInputElement | null) => {
    if (!el) return;
    try {
      // Chromium-based browsers support showPicker() for date/time inputs.
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
        el.click();
      }
    } catch {
      el.focus();
      el.click();
    }
  };

  const loadNotificationPrefs = async () => {
    if (!isPatient) return;
    try {
      setNotifLoading(true);
      const res: any = await AxiosInstance().get(
        "/notifications/patient/me/preferences",
      );
      // Backend response shape:
      // { success, message, data: { preferences: { ... }, patientId } }
      const p = res?.data?.data?.preferences ?? res?.data?.data ?? {};

      setNotifTimezone(p?.timezone ?? "");
      setSmsEnabled(p?.smsEnabled ?? true);
      setEmailEnabled(p?.emailEnabled ?? false);
      setClearSnooze(p?.clearSnooze ?? false);

      setQuietHoursEnabled(!!p?.quietHoursEnabled);
      setQuietHoursStartTime(p?.quietHoursStartTime ?? "");
      setQuietHoursEndTime(p?.quietHoursEndTime ?? "");

      // Backend can return snooze window via snoozeStartAt/snoozeUntil (no preset).
      // Keep preset blank unless API provides one.
      setSnoozePreset(p?.snoozePreset ?? p?.lastSnoozePreset ?? "");
      // For custom window, backend uses ISO in startDateTime/endDateTime.
      // We don't attempt perfect tz conversion in UI-only flow.
      setSnoozeCustomStartLocal(p?.snoozeStartAt ?? "");
      setSnoozeCustomEndLocal(p?.snoozeUntil ?? "");

      const ep = p?.eventPreferences ?? {};
      setEventPrefs({
        NO_RPM_READING_3_DAYS: ep?.NO_RPM_READING_3_DAYS?.enabled ?? true,
        NO_CARE_PLAN_MONTHLY: ep?.NO_CARE_PLAN_MONTHLY?.enabled ?? true,
        RPM_ACHIEVEMENT_16PLUS: ep?.RPM_ACHIEVEMENT_16PLUS?.enabled ?? true,
        DEVICE_READING_RECEIVED: ep?.DEVICE_READING_RECEIVED?.enabled ?? true,
        DEVICE_READING_RECEIVED_sendDuringQuietHours:
          ep?.DEVICE_READING_RECEIVED?.sendDuringQuietHours ?? false,
      });
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? "Failed to load notification preferences",
      );
    } finally {
      setNotifLoading(false);
    }
  };

  const saveNotificationPrefs = async () => {
    if (!isPatient) return;
    try {
      setNotifSaving(true);

      const payload: any = {
        timezone: notifTimezone || undefined,
        smsEnabled,
        emailEnabled,
        quietHoursEnabled,
        quietHoursStartTime: quietHoursEnabled ? quietHoursStartTime : undefined,
        quietHoursEndTime: quietHoursEnabled ? quietHoursEndTime : undefined,
        eventPreferences: {
          NO_RPM_READING_3_DAYS: { enabled: !!eventPrefs.NO_RPM_READING_3_DAYS },
          NO_CARE_PLAN_MONTHLY: { enabled: !!eventPrefs.NO_CARE_PLAN_MONTHLY },
          RPM_ACHIEVEMENT_16PLUS: { enabled: !!eventPrefs.RPM_ACHIEVEMENT_16PLUS },
          DEVICE_READING_RECEIVED: {
            enabled: !!eventPrefs.DEVICE_READING_RECEIVED,
            // Informational only (not user-controlled in UI)
            sendDuringQuietHours:
              !!eventPrefs.DEVICE_READING_RECEIVED_sendDuringQuietHours,
          },
        },
        snoozePreset: snoozePreset || undefined,
        clearSnooze: clearSnooze,
      };

      if (snoozePreset === "custom") {
        const startDateTime = toIsoOrEmpty(snoozeCustomStartLocal);
        const endDateTime = toIsoOrEmpty(snoozeCustomEndLocal);
        payload.startDateTime = startDateTime || undefined;
        payload.endDateTime = endDateTime || undefined;
      }

      await AxiosInstance().patch(
        "/notifications/patient/me/preferences",
        payload,
      );

      toast.success("Notification preferences saved");
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? "Failed to save notification preferences",
      );
    } finally {
      setNotifSaving(false);
    }
  };

  const handleOptOutSubmit = async(formdata: any) => {
    setLoadingOptOut(true);
    const {pr, rejectFn, resolveFn} = getPromise()
    getPromiseToast(pr)
  try {
    const data :any = await AxiosInstance().post("/program-removal/request", {...formdata, program : formdata?.program?.join(',')});
    // console.log("data -> ", data);
    resolveFn(data?.data?.message ?? "Request submitted successfully");

    setOptReason("");
  setOpenOptOut(false);
  } catch (error) {
    rejectFn(error)
  }
  finally{
    setLoadingOptOut(false);
  }
  };

  useEffect(() => {
    const root = document.documentElement;

    switch (fontSize) {
      case "small":
        root.style.fontSize = "14px";
        break;
      case "medium":
        root.style.fontSize = "16px";
        break;
      case "large":
        root.style.fontSize = "18px";
        break;
      case "extra-large":
        root.style.fontSize = "20px";
        break;
    }
    localStorage.setItem("fontSize", fontSize);

    if (highContrast) root.classList.add("high-contrast");
    else root.classList.remove("high-contrast");

    localStorage.setItem("highContrast", highContrast.toString());

    if (screenReaderMode) root.setAttribute("data-screen-reader", "true");
    else root.removeAttribute("data-screen-reader");

    localStorage.setItem("screenReaderMode", screenReaderMode.toString());
  }, [fontSize, highContrast, screenReaderMode]);

  useEffect(() => {
    if (!isPatient) return;
    void loadNotificationPrefs();
  }, [isPatient]);

  const handleUpdateProfile = async (e :any) => {    
    setLoadingProfile(true);
    // e.preventDefault();
    // Disabled fields are omitted from RHF submit data; keep canonical name from auth.
    e = {
      // name: e.name ?? displayName ?? user?.name,
      email: e.email ?? profile?.email,
      // alterEmail: e.alterEmail,
      primaryNumber: e.phone ?? profile?.primaryNumber ?? "",
    };
    
    try {
      const {data} :any = await AxiosInstance().post("/profile", {...e});
      console.log("DATA", data);
      if(!data?.data?.updated) {
        toast.error(data?.message ?? "Failed to update profile");
      }
      else{
      // setUser((prev: any) => ({ ...prev, ...e }));
      setProfile((prev: any) => ( {...prev, ...data?.data} ));
      toast.success(data?.message ?? "Profile updated successfully");
      }
    } catch {
      toast.error("Failed to update profile");
    }
    finally{
      setLoadingProfile(false);
    }
  };

  // 🔐 PASSWORD UPDATE HANDLER (For non-admin users)
  const handlePasswordUpdate = async (formdata : any) => {
   
const {newPassword, confirmPassword} = formdata
    if (!newPassword || !confirmPassword) {
      return toast.error("Please fill all fields");
    }

    if (newPassword !== confirmPassword) {
      return toast.error("Passwords do not match");
    }
const {pr, rejectFn, resolveFn} = getPromise()
getPromiseToast(pr)
    try {
      setLoadingPass(true);

      await AxiosInstance().post("/auth/reset-password", {
        password: newPassword,
      });

      resolveFn("Password updated successfully");

    
    } catch (err: any) {
      rejectFn(err)
    } finally {
      setLoadingPass(false);
    }
  };

  const resetAccessibilitySettings = () => {
    setFontSize("medium");
    setHighContrast(false);
    setScreenReaderMode(false);
    toast.success("Accessibility settings reset to defaults");
  };

  return (
    <DashboardLayout activePage="/profile">
      <div className="space-y-6 sm:space-y-8">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-3xl font-bold tracking-tight">
            Profile Settings
          </h2>
          {hasLinkedProfilesForSwitch && false && (
            <Popover open={isUserSwitchOpen} onOpenChange={setIsUserSwitchOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                >
                  <Repeat className="w-4 h-4 mr-2" />
                  Switch User
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                side="bottom"
                className="w-[min(95vw,560px)] p-0 border-none shadow-none bg-transparent"
              >
                <UserSwitch
                  embedded
                  currentProfile={profile}
                  onClose={() => setIsUserSwitchOpen(false)}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
        <div>
          <p className="text-muted-foreground mt-2">
            Manage your account information and accessibility preferences
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {/* 🧍 PERSONAL INFORMATION */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-center mb-6">
                <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-3xl font-semibold">
                  {(name || user?.email || "U").charAt(0).toUpperCase()}
                </div>
              </div>
<RHFWrapper 
className="space-y-3"
isValidate={true}
validateSchema={yup.object({
  name : yup.string().required("Name is required"),
  email: yup
    .string()
    .email("Email must be valid")
    .required("Email is required")
    .trim(),
  // alterEmail: yup
  //   .string()
  //   .email("Alternate email must be valid")
  //   .trim(),
phone: yup
  .string()
  .required("Phone Number is required")
  .phone("Invalid Phone Number", "Invalid Phone Number"),
})}
onSubmit={handleUpdateProfile}
initialValues={{
name : displayName || profile?.name,
email : profile?.email ?? user?.email ?? "",
phone : profile?.primaryNumber ?? profile?.phone ?? user?.phone ?? "",
// role : user?.role,

}}

>

<RhfInput
name="name"
label="Full Name"
required
isAlphabet
disabled
/>
<RhfInput
name="email"
label="Email Address"
required
type="email"
defaultvalue={profile?.email ?? user?.email ?? ""}
// disabled
/>

{user?.role != "admin" && <>

{/* <RhfInput
name="alterEmail"
label="Alternate Email Address"

type="email"

/> */}
<RhfPhone
name="phone"
label="Phone Number"
required
defaultvalue={profile?.primaryNumber ?? profile?.phone ?? user?.phone ?? ""}
/>
</>}
{/* <RhfInput
name="role"
label="Role"
required
disabled
/> */}

              {/* <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <Input value={user?.email} disabled className="flex-1" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Role</Label>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-muted-foreground" />
                    <Input
                      defaultValue={user?.role}
                      disabled
                      className="flex-1"
                    />
                  </div>
                </div> */}

                <Button type="submit" disabled={loadingProfile} className="w-full mt-4">
                  {loadingProfile ? "Updating..." : "Update Profile"}
                </Button>
</RHFWrapper>

              {/* </form> */}
            </CardContent>
          </Card>

          {/* 🔐 PASSWORD UPDATE — Only For NON-ADMIN */}
          {user?.role?.toLowerCase() !== "admin" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Update Password
                </CardTitle>
              </CardHeader>

              <CardContent>
                <RHFWrapper className="space-y-4" initialValues={{}} onSubmit={(data)=>handlePasswordUpdate(data)} isValidate={false} validateSchema={{}} >
                  <RhfInput name="newPassword" label="New Password" type="password" required />
                  <RhfInput name="confirmPassword" label="Confirm Password" type="password" required />
                  <Button className="w-full"  type="submit" disabled={loadingPass}>
                    {loadingPass ? "Updating..." : "Update Password"}
                  </Button>
           

         
                      </RHFWrapper>
              </CardContent>
            </Card>
          )}

          {/* 🔔 PATIENT NOTIFICATION SETTINGS */}
          {isPatient && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notification Settings
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Control channel, snooze, quiet hours and which events you want to receive.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {notifLoading ? (
                  <p className="text-sm text-muted-foreground">Loading preferences…</p>
                ) : (
                  <>
                    <div className="space-y-1">
                      {/* <Label>Channel</Label> */}
                      <div className="space-y-3 ">
                      <div className="flex items-center justify-between">
                          <div className="text-sm font-medium">Email</div>
                          <Switch checked={emailEnabled} onCheckedChange={setEmailEnabled} />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium">SMS</div>
                          <Switch checked={smsEnabled} onCheckedChange={setSmsEnabled} />
                        </div>
                        {/* <p className="text-xs text-muted-foreground">
                          Disabling a channel is indefinite until you re-enable it.
                        </p> */}
                      </div>
                    </div>

                    <div className="space-y-1">
                      {/* <Label>Time & Snooze</Label> */}
                      <Label>Snooze</Label>
                      <div
                        className={cn(
                          "grid grid-cols-1 gap-3",
                          snoozePreset === "custom" ? "md:grid-cols-2" : "md:grid-cols-2",
                        )}
                      >
                        {/* <div className="space-y-1">
                          <Label>Timezone</Label>
                          {(() => {
                            const options = [...PATIENT_TIMEZONES];
                            const hasCurrent =
                              !!notifTimezone &&
                              options.some((o) => o.value === notifTimezone);
                            const allOptions = hasCurrent
                              ? options
                              : notifTimezone
                                ? [
                                    { label: `${notifTimezone} (current)`, value: notifTimezone },
                                    ...options,
                                  ]
                                : options;

                            return (
                              <Select
                                value={notifTimezone || ""}
                                onValueChange={(v) => setNotifTimezone(v)}
                              >
                                <SelectTrigger className="h-10 w-full">
                                  <SelectValue placeholder="Select timezone" />
                                </SelectTrigger>
                                <SelectContent
                                  position="popper"
                                  className="max-h-[280px] z-[10001]"
                                >
                                  {allOptions.map((tz) => (
                                    <SelectItem key={tz.value} value={tz.value}>
                                      {tz.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          })()}
                        </div> */}

                        <div className="flex items-center justify-between gap-2">
                          {/* <Label>Snooze</Label> */}
                          <Select
                            value={snoozePreset ||  ""}
                            onValueChange={(v) => setSnoozePreset(v as any)}
                          >
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Select snooze" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1h">1h</SelectItem>
                              <SelectItem value="4h">4h</SelectItem>
                              <SelectItem value="12h">12h</SelectItem>
                              <SelectItem value="24h">24h</SelectItem>
                              <SelectItem value="custom">Custom (Date & Time)</SelectItem>
                            </SelectContent>
                          </Select>
                          {(snoozePreset ||
    snoozeCustomStartLocal ||
    snoozeCustomEndLocal) ? (
    <Button
      onClick={() => {
          setSnoozePreset("");
          setSnoozeCustomStartLocal(""),
          setSnoozeCustomEndLocal("")
          setClearSnooze((pre) => !pre);
        }}
      // className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
      aria-label="Clear"
    >
      Clear
    </Button>
  ) : null}
                          {/* <p className="text-xs text-muted-foreground mt-1">
                            Snooze suppresses notifications then auto-resumes.
                          </p> */}
                        </div>

                        {snoozePreset === "custom" ? (
                          <div className="space-y-3 md:col-span-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label>Start</Label>
                                <SnoozeCustomDateTimeField
                                  id="patient-snooze-start"
                                  value={snoozeCustomStartLocal}
                                  onChange={setSnoozeCustomStartLocal}
                                  timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"}
                                  disablePast={true}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label>End</Label>
                                <SnoozeCustomDateTimeField
                                  id="patient-snooze-end"
                                  value={snoozeCustomEndLocal}
                                  onChange={setSnoozeCustomEndLocal}
                                  timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"}
                                  disablePast={true}
                                />
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="space-y-1">
                      {/* <Label>Quiet Hours</Label> */}
                      <div className="rounded-md border p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium">Quiet Hours</div>
                            {/* <p className="text-xs text-muted-foreground">
                              SMS/Emails suppressed during quiet hours; triage overrides.
                            </p> */}
                          </div>
                          <Switch
                            checked={quietHoursEnabled}
                            onCheckedChange={setQuietHoursEnabled}
                          />
                        </div>

                        {quietHoursEnabled ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <Label>Start</Label>
                              <div
                                className="relative h-10 w-full cursor-pointer rounded-md border border-input bg-background focus-within:ring-ring/50 focus-within:ring-[3px]"
                                onClick={() =>
                                  openNativePicker(quietStartRef.current)
                                }
                              >
                                <div className="pointer-events-none absolute inset-0 z-0 flex items-center px-3 pr-10 text-sm md:text-sm">
                                  {quietHoursStartTime ? (
                                    formatHHmmAs12h(quietHoursStartTime)
                                  ) : (
                                    <span className="text-muted-foreground">
                                      Select time
                                    </span>
                                  )}
                                </div>
                                <input
                                  ref={quietStartRef}
                                  type="time"
                                  className="absolute inset-0 z-1 h-full w-full cursor-pointer opacity-0 date-input-native"
                                  value={quietHoursStartTime}
                                  onClick={() =>
                                    openNativePicker(quietStartRef.current)
                                  }
                                  onFocus={() =>
                                    openNativePicker(quietStartRef.current)
                                  }
                                  onChange={(e) =>
                                    setQuietHoursStartTime(e.target.value)
                                  }
                                  aria-label="Quiet hours start"
                                />
                                <Clock className="pointer-events-none absolute right-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label>End</Label>
                              <div
                                className="relative h-10 w-full cursor-pointer rounded-md border border-input bg-background focus-within:ring-ring/50 focus-within:ring-[3px]"
                                onClick={() =>
                                  openNativePicker(quietEndRef.current)
                                }
                              >
                                <div className="pointer-events-none absolute inset-0 z-0 flex items-center px-3 pr-10 text-sm md:text-sm">
                                  {quietHoursEndTime ? (
                                    formatHHmmAs12h(quietHoursEndTime)
                                  ) : (
                                    <span className="text-muted-foreground">
                                      Select time
                                    </span>
                                  )}
                                </div>
                                <input
                                  ref={quietEndRef}
                                  type="time"
                                  className="absolute inset-0 z-1 h-full w-full cursor-pointer opacity-0 date-input-native"
                                  value={quietHoursEndTime}
                                  onClick={() =>
                                    openNativePicker(quietEndRef.current)
                                  }
                                  onFocus={() =>
                                    openNativePicker(quietEndRef.current)
                                  }
                                  onChange={(e) =>
                                    setQuietHoursEndTime(e.target.value)
                                  }
                                  aria-label="Quiet hours end"
                                />
                                <Clock className="pointer-events-none absolute right-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label>Event Preferences</Label>
                      <div className="rounded-md border p-3 space-y-4">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={eventPrefs.NO_RPM_READING_3_DAYS}
                            onCheckedChange={(v) =>
                              setEventPrefs((p) => ({
                                ...p,
                                NO_RPM_READING_3_DAYS: v === true,
                              }))
                            }
                          />
                          <div>
                            <div className="text-sm font-medium">No RPM Reading - 3 Days</div>
                              {/* <div className="text-xs text-muted-foreground">
                                Access control: send this notification.
                              </div> */}
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={eventPrefs.NO_CARE_PLAN_MONTHLY}
                            onCheckedChange={(v) =>
                              setEventPrefs((p) => ({
                                ...p,
                                NO_CARE_PLAN_MONTHLY: v === true,
                              }))
                            }
                          />
                          <div>
                            <div className="text-sm font-medium">No Care Plan Monthly</div>
                            {/* <div className="text-xs text-muted-foreground">
                              Access control: send this notification.
                            </div> */}
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={eventPrefs.RPM_ACHIEVEMENT_16PLUS}
                            onCheckedChange={(v) =>
                              setEventPrefs((p) => ({
                                ...p,
                                RPM_ACHIEVEMENT_16PLUS: v === true,
                              }))
                            }
                          />
                          <div>
                            <div className="text-sm font-medium">RPM Achievement 16+</div>
                            {/* <div className="text-xs text-muted-foreground">
                              Access control: send this notification.
                            </div> */}
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={eventPrefs.DEVICE_READING_RECEIVED}
                            onCheckedChange={(v) =>
                              setEventPrefs((p) => ({
                                ...p,
                                DEVICE_READING_RECEIVED: v === true,
                              }))
                            }
                          />
                          <div>
                            <div className="text-sm font-medium">Device Reading Received</div>
                            {/* <div className="text-xs text-muted-foreground">
                              Send this recorded device reading notification in quiet hours as well.
                            </div> */}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={saveNotificationPrefs} disabled={notifSaving}>
                        {notifSaving ? "Saving…" : "Save Preferences"}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

        {/* 🦾 ACCESSIBILITY SETTINGS */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Accessibility Settings
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Personalize how the dashboard looks and behaves to match your comfort level.
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Font Size */}
              <div className="space-y-2">
                <Label>Font Size</Label>
                <p className="text-sm text-muted-foreground">
                  Adjust the global typography scale for better readability.
                </p>
                <Select value={fontSize} onValueChange={setFontSize}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                    <SelectItem value="extra-large">Extra Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* High Contrast */}
              {/* <div className="space-y-2">
                <Label>High Contrast Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Increase contrast to make interface elements easier to distinguish.
                </p>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <span className="text-sm font-medium">
                    {highContrast ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    checked={highContrast}
                    onCheckedChange={setHighContrast}
                  />
                </div>
              </div> */}

              {/* Screen Reader Mode */}
              {/* <div className="space-y-2">
                <Label>Screen Reader Assistance</Label>
                <p className="text-sm text-muted-foreground">
                  Add extra cues and aria labels for assistive technologies.
                </p>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <span className="text-sm font-medium">
                    {screenReaderMode ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    checked={screenReaderMode}
                    onCheckedChange={setScreenReaderMode}
                  />
                </div>
              </div> */}

              {/* Reset */}
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={resetAccessibilitySettings}
                  className="w-full"
                >
                  Reset to Defaults
                </Button>
              </div>
            </div>
          </CardContent>
          </Card>
          {canShowProfileSupport(user) && (
            <Card className="border-primary/40 bg-primary/5 shadow-sm ring-1 ring-primary/10">
              <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2 text-primary-dark">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15">
                      <HelpCircle className="h-5 w-5 text-primary-dark" />
                    </span>
                    Help &amp; Support
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Submit a support request and our team will get back to you.
                  </p>
                </div>
                <Button
                  type="button"
                  className="w-full shrink-0 sm:w-auto"
                  onClick={openFreshdeskPopup}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Raise a Ticket
                </Button>
              </CardHeader>
            </Card>
          )}

          {/* ⭐ OPT-OUT SECTION — Only for patients */}
          {user?.role === "patient" && (
            <>
              <Card>
                <CardHeader className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="flex capitalize items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Opt Out from Program
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      You may opt out from your care program anytime.
                    </p>
                  </div>
                  <Button
                    className="w-fit ml-auto"
                    // variant="destructive"
                    onClick={() => setOpenOptOut(true)}
                  >
                    Opt Out
                  </Button>
                </CardHeader>
              </Card>

              {/* ⭐ OPT OUT DIALOG */}
            </>
          )}
        </motion.div>
        <div className="border-t pt-6 text-center text-sm text-muted-foreground">
          Need more details?{" "}
          <span className="space-x-2">
            <a
              href="/terms"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              Terms &amp; Conditions
            </a>
            <span>•</span>
            <a
              href="/privacy"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              Privacy Policy
            </a>
          </span>
        </div>
      </div>
      <Dialog open={openOptOut} onOpenChange={setOpenOptOut}>
        <MyDialogContent heading="Opt Out from Program">
          <div className="space-y-4">
            <RHFWrapper
              initialValues={{ program: [], reason: "" }}
              isValidate={true}
              validateSchema={optOutSchema}
              onSubmit={handleOptOutSubmit}
              className="space-y-4"
            >
              <RhfSelect
                required
                name="program"
                label="Programs"
                multiple
                options={programOptions}
                placeholder="e.g., CCM, RPM"
              />
              <RhfInput name="reason" label="Reason" type="textarea" required />
              <Button disabled={loadingOptOut} className="w-full" type="submit">
                Submit Opt-Out Request
              </Button>
            </RHFWrapper>
        
          </div>
        </MyDialogContent>
        {/* <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Opt Out Request</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Submit your reason for opting out from the program.
          </p>
        </DialogHeader>

   
      </DialogContent> */}
      </Dialog>
    </DashboardLayout>
  );
}
